// Registry of the published articles ("Artikel" / "Articles", under
// /de/guides/ and /en/guides/) and their Quick Click-Throughs. Single source
// for the overview page, the "Weitere Themen" block, breadcrumbs, the nav's
// active state, prerender routes (src/prerenderRoutes.ts) and the generated
// social preview images (scripts/prerender-main-site.mjs).
//
// To publish a new topic: add its article page under src/pages/ (reading time
// is computed from that file, see vite-plugin-reading-time.ts), add an entry
// below, add the routes in App.tsx, and add the article URLs to
// public/sitemap.xml. Overview tile, prerender entry and preview image follow
// from this entry automatically.
//
// Unpublished guides (Kleinunternehmerregelung, Selbstständig werden) are
// deliberately NOT in here — they stay noindex and unlinked.

import { Percent, ShieldCheck, type LucideIcon } from 'lucide-react';
import readingTimes from 'virtual:reading-times';
import { PROGRESS as SVS_FLOW_STEPS } from './prototypes/svs-year-end-v2/flow-short/progress';
import { PROGRESS as VAT_FLOW_STEPS } from './prototypes/vat-basics-v1/flow-short/progress';
import { SITE_URL } from './seoRoutes';

export type ArticleLang = 'de' | 'en';

export interface ArticleVersion {
  path: string;
  shortPath: string;
  // File name of the article component in src/pages/ (key into the
  // build-time reading-time map).
  component: string;
  // Topic tag on the overview tile, breadcrumb label and preview image.
  tag: string;
  h1: string;
  title: string;
  description: string;
  datePublished: string;
  dateModified: string;
}

export interface ArticleTopic {
  key: string;
  icon: LucideIcon;
  // Number of steps of the Quick Click-Through (its progress bar).
  steps: number;
  de: ArticleVersion;
  en: ArticleVersion;
}

// Order = order on the overview page (by topic, not by date).
export const ARTICLE_TOPICS: ArticleTopic[] = [
  {
    key: 'svs',
    icon: ShieldCheck,
    steps: SVS_FLOW_STEPS.length,
    de: {
      path: '/de/guides/svs-sozialversicherung/',
      shortPath: '/de/guides/svs-sozialversicherung/kurz/',
      component: 'SvsSozialversicherungGuide',
      tag: 'SVS',
      h1: 'SVS-Beiträge 2026 und SVS-Nachzahlung: Was Selbständige in Österreich wirklich zahlen',
      title: 'SVS-Beiträge 2026 & SVS-Nachzahlung erklärt | PSTax',
      description:
        'SVS-Beiträge 2026 für Selbständige in Österreich: was du zahlst, wann die SVS-Nachzahlung kommt und wie du sie berechnest. Vom Steuerberater erklärt.',
      datePublished: '2026-09-15',
      dateModified: '2026-11-02',
    },
    en: {
      path: '/en/guides/svs-social-insurance/',
      shortPath: '/en/guides/svs-social-insurance/short/',
      component: 'SvsSocialInsuranceGuide',
      tag: 'SVS',
      h1: 'SVS contributions 2026 and the SVS back payment: what the self-employed in Austria actually pay',
      title: 'SVS contributions 2026 & SVS back payment explained | PSTax',
      description:
        'SVS contributions 2026 for the self-employed in Austria: what you pay, when the SVS back payment comes and how to calculate it. Explained by a tax advisor.',
      datePublished: '2026-09-15',
      dateModified: '2026-11-02',
    },
  },
  {
    key: 'vat',
    icon: Percent,
    steps: VAT_FLOW_STEPS.length,
    de: {
      path: '/de/guides/umsatzsteuer-grundlagen/',
      shortPath: '/de/guides/umsatzsteuer-grundlagen/kurz/',
      component: 'UmsatzsteuerGrundlagenGuide',
      tag: 'Umsatzsteuer',
      h1: 'Umsatzsteuer für Selbstständige in Österreich 2026: was du wirklich verrechnen musst',
      title: 'Umsatzsteuer für Selbstständige in Österreich 2026 | PSTax',
      description:
        'Umsatzsteuer 2026 für Selbstständige in Österreich: Sätze, Kleinunternehmergrenze, Vorsteuerabzug und wann sich der freiwillige Verzicht lohnt. Vom Steuerberater erklärt.',
      datePublished: '2026-10-02',
      dateModified: '2026-11-02',
    },
    en: {
      path: '/en/guides/vat-basics-austria/',
      shortPath: '/en/guides/vat-basics-austria/short/',
      component: 'VatBasicsGuide',
      tag: 'VAT',
      h1: 'VAT in Austria 2026: what self-employed people actually have to charge',
      title: 'VAT Basics for the Self-Employed in Austria 2026 | PSTax',
      description:
        'VAT in Austria 2026 for the self-employed: rates, the small-business exemption, input tax deduction, and when opting in voluntarily is worth it. Explained by a tax advisor.',
      datePublished: '2026-10-02',
      dateModified: '2026-11-02',
    },
  },
];

export function getTopic(key: string): ArticleTopic {
  const topic = ARTICLE_TOPICS.find((t) => t.key === key);
  if (!topic) throw new Error(`articles: no topic "${key}"`);
  return topic;
}

export const ARTICLES_OVERVIEW = {
  de: {
    path: '/de/guides/',
    title: 'Wissen für Selbstständige in Österreich | PSTax',
    description:
      'SVS, Umsatzsteuer und Gründung verständlich erklärt: Artikel und Quick Click-Throughs vom Steuerberater für Selbstständige in Österreich.',
    h1: 'Wissen für Selbstständige in Österreich',
    intro:
      'SVS, Umsatzsteuer, Gründung: Die wichtigsten Themen für Selbstständige, verständlich erklärt von deinem Steuerberater – ohne Fachchinesisch und laufend aktualisiert.',
    previewTag: 'Artikel',
  },
  en: {
    path: '/en/guides/',
    title: 'Know-how for the Self-Employed in Austria | PSTax',
    description:
      'SVS, VAT and starting out explained clearly: articles and quick click-throughs from a tax advisor for the self-employed in Austria.',
    h1: 'Know-how for the self-employed in Austria',
    intro:
      'SVS, VAT, starting out: the key topics for the self-employed in Austria, explained clearly by your tax advisor – no jargon, and always up to date.',
    previewTag: 'Articles',
  },
} as const;

// UI copy shared by the overview, the article pages and the click-throughs.
export const ARTICLES_UI = {
  de: {
    home: 'Startseite',
    homePath: '/de/',
    articles: 'Artikel',
    quick: 'Quick Click-Through',
    readArticle: 'Artikel lesen',
    interactiveSteps: (n: number) => `Interaktiv · ${n} Schritte`,
    updated: 'Aktualisiert:',
    readingTime: (min: number) => `${min} Min. Lesezeit`,
    moreSoon: 'Weitere Themen folgen.',
    toQuick: 'Keine Zeit? Zum Quick Click-Through →',
    toArticle: 'Zum ausführlichen Artikel →',
    moreTopics: 'Weitere Themen',
    fitCheckTitle: 'Lieber persönlich klären?',
    fitCheckText: 'Mach den Fit Check in 2 Minuten und buch dein kostenloses Erstgespräch.',
    fitCheckCta: 'Fit Check starten →',
    fitCheckPath: '/de/#fit-check',
    breadcrumbAria: 'Breadcrumb',
  },
  en: {
    home: 'Home',
    homePath: '/en/',
    articles: 'Articles',
    quick: 'Quick Click-Through',
    readArticle: 'Read article',
    interactiveSteps: (n: number) => `Interactive · ${n} steps`,
    updated: 'Updated:',
    readingTime: (min: number) => `${min} min read`,
    moreSoon: 'More topics coming soon.',
    toQuick: 'Short on time? Try the Quick Click-Through →',
    toArticle: 'Read the full article →',
    moreTopics: 'More topics',
    fitCheckTitle: 'Rather talk it through?',
    fitCheckText: 'Take the 2-minute Fit Check and book your free initial consultation.',
    fitCheckCta: 'Start Fit Check →',
    fitCheckPath: '/en/#fit-check',
    breadcrumbAria: 'Breadcrumb',
  },
} as const;

export function readingMinutes(v: ArticleVersion): number {
  return readingTimes[v.component] ?? 1;
}

// "Oktober 2026" / "October 2026" — fixed month names instead of
// Intl/toLocaleDateString so server and client render the same string.
const MONTHS: Record<ArticleLang, string[]> = {
  de: ['Jänner', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
};

export function formatMonthYear(isoDate: string, lang: ArticleLang): string {
  const [y, mth] = isoDate.split('-').map(Number);
  return `${MONTHS[lang][mth - 1]} ${y}`;
}

export interface Crumb {
  label: string;
  path?: string; // omitted for the current page
}

export function overviewCrumbs(lang: ArticleLang): Crumb[] {
  const ui = ARTICLES_UI[lang];
  return [{ label: ui.home, path: ui.homePath }, { label: ui.articles }];
}

export function articleCrumbs(topic: ArticleTopic, lang: ArticleLang, short = false): Crumb[] {
  const ui = ARTICLES_UI[lang];
  const v = topic[lang];
  const base: Crumb[] = [
    { label: ui.home, path: ui.homePath },
    { label: ui.articles, path: ARTICLES_OVERVIEW[lang].path },
  ];
  return short
    ? [...base, { label: v.tag, path: v.path }, { label: ui.quick }]
    : [...base, { label: v.tag }];
}

export function breadcrumbJsonLd(crumbs: Crumb[], currentPath: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.label,
      item: `${SITE_URL}${c.path ?? currentPath}`,
    })),
  };
}

export function overviewJsonLd(lang: ArticleLang) {
  const o = ARTICLES_OVERVIEW[lang];
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: o.h1,
    description: o.description,
    url: `${SITE_URL}${o.path}`,
    inLanguage: lang,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: ARTICLE_TOPICS.map((t, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `${SITE_URL}${t[lang].path}`,
        name: t[lang].h1,
      })),
    },
  };
}

// DE/EN alternates; x-default = German, the site default (same as seoRoutes.ts).
export function hreflangPair(dePath: string, enPath: string) {
  return [
    { lang: 'en', href: `${SITE_URL}${enPath}` },
    { lang: 'de', href: `${SITE_URL}${dePath}` },
    { lang: 'x-default', href: `${SITE_URL}${dePath}` },
  ];
}

// Generated social preview image (1200×630) for a route — written at build
// time by scripts/prerender-main-site.mjs for every prerender route that has
// `preview` set (see prerenderRoutes.ts).
export function previewImagePath(path: string): string {
  return `/og/${path.replace(/^\/|\/$/g, '').replace(/\//g, '-')}.png`;
}

// Which article section the current pathname belongs to — drives the nav's
// active state. True for the overview, every article and every click-through.
export function isArticlesPath(pathname: string): boolean {
  return /^\/(de|en)\/guides(\/|$)/.test(pathname);
}
